import { useCallback, useEffect, useRef } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { now } from "@/shared/utils/date/dateUtils"

const SETTING_KEY = "#sidebar-width"
const LS_KEY = "totodo:sidebar-width"
const DEFAULT_WIDTH = 300 // pixels — matches v1
const MIN_WIDTH = 200 // pixels
const MAX_WIDTH = 500 // pixels

/** Synchronous read from localStorage — instant, no DB wait */
function readCachedWidth(): number {
  try {
    const val = localStorage.getItem(LS_KEY)
    if (val) {
      const n = Number(val)
      if (n >= MIN_WIDTH && n <= MAX_WIDTH) return n
    }
  } catch {}
  return DEFAULT_WIDTH
}

/**
 * Persist and read sidebar width (in pixels) from Dexie `setting_local`,
 * with localStorage mirror for instant first-render (no width jump).
 *
 * Returns `{ width, setWidth, minWidth, maxWidth, defaultWidth }`.
 * All values are in pixels.
 */
export function useSidebarWidth() {
  const cachedWidth = useRef(readCachedWidth())

  const [row] = useLiveQueryImmediate(
    () => db.setting_local.get(SETTING_KEY),
    ["sidebar-width"],
    undefined,
  )

  const width = row?.value ? Number(row.value) : cachedWidth.current

  // Mirror to localStorage whenever Dexie value changes
  useEffect(() => {
    if (row?.value) {
      try {
        localStorage.setItem(LS_KEY, row.value)
      } catch {}
    }
  }, [row?.value])

  // Debounce writes — don't hammer IndexedDB on every drag frame
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setWidth = useCallback((px: number) => {
    const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(px)))
    // Write to localStorage immediately (synchronous, no flicker on next load)
    try {
      localStorage.setItem(LS_KEY, String(clamped))
    } catch {}
    // Debounce Dexie write
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      db.setting_local.put({
        id: SETTING_KEY,
        value: String(clamped),
        device: "local",
        createdAt: now(),
        createdBy: "local",
        updatedAt: now(),
      })
    }, 300)
  }, [])

  // Clean up timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  return {
    width,
    setWidth,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    defaultWidth: DEFAULT_WIDTH,
  } as const
}
